import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useContent, useLang } from "../i18n/LanguageContext";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const c = useContent();
  const t = c.chat;
  const { lang } = useLang();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const sessionRef = useRef(Math.random().toString(36).slice(2) + Date.now().toString(36));
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionRef.current,
        message: msg,
        lang,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: t.error }]);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const showFaqs = messages.length === 0;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t.open}
          className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full btn-gold flex items-center justify-center shadow-xl"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[70] w-[calc(100vw-3rem)] sm:w-[380px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-[#e7e2d8] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-[#16233d] px-5 py-4 flex items-start justify-between">
            <div>
              <div className="font-serif-lux text-xl text-white leading-tight">{t.title}</div>
              <div className="text-xs text-white/60 mt-0.5">{t.subtitle}</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label={t.close} className="text-white/70 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#faf8f4]">
            <div className="bg-white rounded-xl rounded-tl-sm border border-[#efe9dd] px-4 py-3 text-sm text-[#3a4258] leading-relaxed max-w-[90%]">
              {t.greeting}
            </div>

            {showFaqs && (
              <div className="pt-1">
                <div className="text-xs text-[#8a8f9c] mb-2">{t.faqLabel}</div>
                <div className="flex flex-col gap-2">
                  {t.faqs.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="text-left text-sm text-[#16233d] rounded-full border border-[#e0d6c4] bg-white px-4 py-2 hover:border-[#b08d57] hover:bg-[#faf3e8] transition-colors w-fit max-w-full"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`px-4 py-3 text-sm leading-relaxed max-w-[90%] whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#16233d] text-white rounded-xl rounded-tr-sm"
                      : "bg-white text-[#3a4258] border border-[#efe9dd] rounded-xl rounded-tl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#efe9dd] rounded-xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#c9b28a] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#c9b28a] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#c9b28a] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-[#eee] bg-white flex items-center gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.placeholder}
              className="flex-1 rounded-full border border-[#e7e2d8] px-4 py-2.5 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
              className="w-11 h-11 shrink-0 rounded-full btn-gold flex items-center justify-center disabled:opacity-50"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
